import React, { useState, useEffect, useRef } from "react";
import { Menu, X, Users, Vote, Calendar, BarChart3, Building2, LogOut, User, ChevronDown, FileText, Sparkles, FolderKanban } from "lucide-react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import Logo from "../../assets/images/bjp-logo.webp";
import { useAuth } from '../../context/AuthContext';

function Header() {
    const { user, logout, isAuthenticated, loading } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showElectionDropdown, setShowElectionDropdown] = useState(false);
    const [mobileElectionOpen, setMobileElectionOpen] = useState(true);

    const dropdownRef = useRef(null);
    const userMenuRef = useRef(null);

    const handleLogout = () => {
        logout();
        navigate('/');
        setMobileMenuOpen(false);
        setShowUserMenu(false);
    };

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowElectionDropdown(false);
            }
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setShowUserMenu(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Close mobile menu on route change
    useEffect(() => {
        setMobileMenuOpen(false);
        setShowElectionDropdown(false);
    }, [location.pathname]);

    // Grouped election & booth items as requested by user
    const electionMenuItems = [
        { icon: Vote, label: "வாக்காளர் பட்டியல்", path: "/vote_booths" },
        { icon: BarChart3, label: "தேர்தல் முடிவுகள்", path: "/election_results" },
        { icon: Users, label: "பூத் குழு விபரங்கள்", path: "/booth_committee" },
        { icon: FileText, label: "படிவங்கள்", path: "/forms" }
    ];

    const isElectionActive = electionMenuItems.some(item => location.pathname === item.path);

    const mainNavItems = [
        { icon: Users, label: "நிர்வாகிகள்", path: "/administrators" },
        { icon: Sparkles, label: "நிகழ்வுகள்", path: "/events" },
        { icon: Calendar, label: "முக்கிய நிகழ்வுகள்", path: "/calendar" },
        { icon: Building2, label: "வளர்ச்சித் திட்டங்கள்", path: "/development_projects" }
    ];

    const getUserDisplay = () => {
        if (!user) return '';
        const userName = user.name || user.fullName || user.username;
        if (userName) return userName;
        if (user.email) {
            const emailName = user.email.split('@')[0];
            return emailName.charAt(0).toUpperCase() + emailName.slice(1);
        }
        return 'User';
    };

    if (loading) {
        return (
            <nav className="bg-white shadow-md fixed w-full top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <img src={Logo} alt="Logo" className="h-12 w-12 object-contain rounded-full shadow-md" />
                        <div className="animate-pulse bg-gray-200 h-8 w-32 rounded"></div>
                    </div>
                </div>
            </nav>
        );
    }

    return (
        <nav className="bg-white shadow-md fixed w-full top-0 z-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-16">

                    {/* Logo */}
                    <NavLink to="/" className="flex items-center space-x-2">
                        <img
                            src={Logo}
                            alt="Logo"
                            className="h-11 w-11 object-contain rounded-full shadow-md hover:scale-105 transition-transform"
                        />
                        <span className="font-bold text-gray-800 text-sm hidden sm:inline-block md:hidden lg:inline-block">
                          BJP கிணத்துக்கடவு
                        </span>
                    </NavLink>

                    {/* Desktop Navigation Menu */}
                    <div className="hidden md:flex items-center space-x-1 lg:space-x-2">
                        {/* Main Items before Dropdown */}
                        {mainNavItems.slice(0, 3).map((item) => (
                            <NavLink
                                key={item.label}
                                to={item.path}
                                className={({ isActive }) =>
                                    `flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150
                                    ${isActive
                                        ? "text-orange-600 bg-orange-50 font-semibold shadow-sm"
                                        : "text-gray-700 hover:text-orange-600 hover:bg-orange-50/70"
                                    }`
                                }
                            >
                                <item.icon className="w-4 h-4 mr-1.5 flex-shrink-0" />
                                <span>{item.label}</span>
                            </NavLink>
                        ))}

                        {/* Grouped Dropdown for Elections, Voters, Booth Committee & Forms */}
                        <div className="relative" ref={dropdownRef}>
                            <button
                                onClick={() => setShowElectionDropdown(!showElectionDropdown)}
                                onMouseEnter={() => setShowElectionDropdown(true)}
                                className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150
                                    ${isElectionActive || showElectionDropdown
                                        ? "text-orange-600 bg-orange-50 font-semibold shadow-sm"
                                        : "text-gray-700 hover:text-orange-600 hover:bg-orange-50/70"
                                    }`}
                            >
                                <FolderKanban className="w-4 h-4 mr-1.5 flex-shrink-0 text-orange-500" />
                                <span>தேர்தல் & பூத் விபரங்கள்</span>
                                <ChevronDown className={`w-4 h-4 ml-1 transition-transform duration-200 ${showElectionDropdown ? 'rotate-180 text-orange-600' : 'text-gray-400'}`} />
                            </button>

                            {/* Dropdown Menu Popup */}
                            {showElectionDropdown && (
                                <div 
                                    onMouseLeave={() => setShowElectionDropdown(false)}
                                    className="absolute left-0 mt-1 w-56 bg-white rounded-xl shadow-xl py-2 border border-gray-100 z-50 animate-in fade-in zoom-in-95 duration-150"
                                >
                                    <div className="px-3 py-1.5 border-b border-gray-100 text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                                        தேர்தல் & பூத் பணிகள்
                                    </div>
                                    {electionMenuItems.map((item) => (
                                        <NavLink
                                            key={item.label}
                                            to={item.path}
                                            onClick={() => setShowElectionDropdown(false)}
                                            className={({ isActive }) =>
                                                `flex items-center px-4 py-2.5 text-sm transition-colors
                                                ${isActive
                                                    ? "text-orange-600 bg-orange-50 font-semibold"
                                                    : "text-gray-700 hover:text-orange-600 hover:bg-orange-50/50"
                                                }`
                                            }
                                        >
                                            <item.icon className="w-4 h-4 mr-2.5 text-orange-500 flex-shrink-0" />
                                            <span>{item.label}</span>
                                        </NavLink>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Remaining Main Items */}
                        {mainNavItems.slice(3).map((item) => (
                            <NavLink
                                key={item.label}
                                to={item.path}
                                className={({ isActive }) =>
                                    `flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150
                                    ${isActive
                                        ? "text-orange-600 bg-orange-50 font-semibold shadow-sm"
                                        : "text-gray-700 hover:text-orange-600 hover:bg-orange-50/70"
                                    }`
                                }
                            >
                                <item.icon className="w-4 h-4 mr-1.5 flex-shrink-0" />
                                <span>{item.label}</span>
                            </NavLink>
                        ))}

                        {/* User Profile Menu - Desktop */}
                        {isAuthenticated && user && (
                            <div className="relative ml-2" ref={userMenuRef}>
                                <button
                                    onClick={() => setShowUserMenu(!showUserMenu)}
                                    className="flex items-center space-x-2 px-3 py-1.5 rounded-lg border border-gray-200 hover:border-orange-200 hover:bg-orange-50/50 transition-colors"
                                >
                                    <div className="w-7 h-7 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full flex items-center justify-center shadow-sm">
                                        <User className="w-3.5 h-3.5 text-white" />
                                    </div>
                                    <span className="text-xs font-semibold text-gray-700 max-w-[100px] truncate">
                                        {getUserDisplay()}
                                    </span>
                                    <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
                                </button>
                                
                                {showUserMenu && (
                                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl py-2 border border-gray-100 z-50">
                                        <div className="px-4 py-2 border-b border-gray-100">
                                            <p className="text-sm font-medium text-gray-900 truncate">
                                                {user.name || user.fullName || 'User'}
                                            </p>
                                            <p className="text-xs text-gray-500 truncate">
                                                {user.email}
                                            </p>
                                        </div>
                                        <button
                                            onClick={handleLogout}
                                            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center space-x-2 font-medium"
                                        >
                                            <LogOut className="h-4 w-4" />
                                            <span>வெளியேறு (Logout)</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Mobile menu toggle button */}
                    <div className="md:hidden flex items-center space-x-2">
                        {isAuthenticated && user && (
                            <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full flex items-center justify-center shadow-sm">
                                <User className="w-4 h-4 text-white" />
                            </div>
                        )}
                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="p-2 rounded-lg text-gray-700 hover:text-orange-600 hover:bg-orange-50 transition-colors"
                        >
                            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Navigation Drawer */}
            {mobileMenuOpen && (
                <div className="md:hidden bg-white border-t shadow-xl max-h-[85vh] overflow-y-auto">
                    <div className="px-3 pt-3 pb-4 space-y-1">
                        {/* User Info */}
                        {isAuthenticated && user && (
                            <div className="px-3 py-3 mb-2 bg-gradient-to-r from-orange-50 to-orange-100/80 rounded-xl border border-orange-200/50">
                                <div className="flex items-center space-x-3">
                                    <div className="w-9 h-9 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full flex items-center justify-center shadow">
                                        <User className="w-5 h-5 text-white" />
                                    </div>
                                    <div className="overflow-hidden">
                                        <p className="text-sm font-bold text-gray-900 truncate">
                                            {user.name || user.fullName || getUserDisplay()}
                                        </p>
                                        <p className="text-xs text-gray-600 truncate">
                                            {user.email}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {/* Main Menu Items */}
                        {mainNavItems.slice(0, 3).map((item) => (
                            <NavLink
                                key={item.label}
                                to={item.path}
                                onClick={() => setMobileMenuOpen(false)}
                                className={({ isActive }) =>
                                    `flex items-center px-3.5 py-2.5 rounded-lg text-sm font-medium transition-colors
                                    ${isActive
                                        ? "text-orange-600 bg-orange-50 font-semibold"
                                        : "text-gray-700 hover:text-orange-600 hover:bg-orange-50"
                                    }`
                                }
                            >
                                <item.icon className="w-4 h-4 mr-3 text-orange-500" />
                                <span>{item.label}</span>
                            </NavLink>
                        ))}

                        {/* Collapsible Election Items on Mobile */}
                        <div className="py-1">
                            <button
                                onClick={() => setMobileElectionOpen(!mobileElectionOpen)}
                                className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                    isElectionActive ? "bg-orange-50 text-orange-600 font-semibold" : "text-gray-800 hover:bg-gray-50"
                                }`}
                            >
                                <div className="flex items-center">
                                    <FolderKanban className="w-4 h-4 mr-3 text-orange-500" />
                                    <span>தேர்தல் & பூத் விபரங்கள்</span>
                                </div>
                                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${mobileElectionOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {mobileElectionOpen && (
                                <div className="ml-4 pl-3 border-l-2 border-orange-200 mt-1 space-y-1">
                                    {electionMenuItems.map((item) => (
                                        <NavLink
                                            key={item.label}
                                            to={item.path}
                                            onClick={() => setMobileMenuOpen(false)}
                                            className={({ isActive }) =>
                                                `flex items-center px-3 py-2 rounded-lg text-sm transition-colors
                                                ${isActive
                                                    ? "text-orange-600 bg-orange-50 font-semibold"
                                                    : "text-gray-600 hover:text-orange-600 hover:bg-orange-50"
                                                }`
                                            }
                                        >
                                            <item.icon className="w-4 h-4 mr-2.5 text-orange-500" />
                                            <span>{item.label}</span>
                                        </NavLink>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Remaining Main Items */}
                        {mainNavItems.slice(3).map((item) => (
                            <NavLink
                                key={item.label}
                                to={item.path}
                                onClick={() => setMobileMenuOpen(false)}
                                className={({ isActive }) =>
                                    `flex items-center px-3.5 py-2.5 rounded-lg text-sm font-medium transition-colors
                                    ${isActive
                                        ? "text-orange-600 bg-orange-50 font-semibold"
                                        : "text-gray-700 hover:text-orange-600 hover:bg-orange-50"
                                    }`
                                }
                            >
                                <item.icon className="w-4 h-4 mr-3 text-orange-500" />
                                <span>{item.label}</span>
                            </NavLink>
                        ))}
                        
                        {/* Logout Button */}
                        {isAuthenticated && (
                            <button
                                onClick={handleLogout}
                                className="w-full flex items-center space-x-2 px-3.5 py-2.5 mt-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors font-medium text-sm"
                            >
                                <LogOut className="h-4 w-4" />
                                <span>வெளியேறு (Logout)</span>
                            </button>
                        )}
                    </div>
                </div>
            )}
        </nav>
    );
}

export default Header;